/**
 * @file   src\Pages\CWOTransactionHistory.tsx
 * @brief  TransactionHistory page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Table, Breadcrumb, Button, Dropdown, useOutletContext, useNavigate, useEffect, useState, Pagination, React } from '../utils/thirdpartyComponents';
import { AppContextType, formValueInterface } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import "../components/Dropdown.scss";
import FilterIcon from '../assets/images/icons/filter';
import CWRadio from '../components/CWRadio';
import CWSelect from "../components/CWSelect";
import { ChangeEvent } from 'react';
import DatePicker from 'react-datepicker';
import SmallLoader from '../components/SmallLoader';
import axios from '../config/axiosConfig';
import { LocationApi, TransactionApi } from '../utils/apiUrls';
import moment from "moment-timezone"
import { TransactionItem } from '../interfaces/hardwareInterface';
import { ItemTypeEnum } from '../utils/enums';
import { LocationItem, Option } from "../interfaces/subscriptionInterface";


const CWOTransactionHistory = () => {
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const [filterClass, setFilterClass] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [locations, setLocations] = useState<Option[]>([]);
    const [selectedLocationId, setSelectedLocationId] = useState("0");
    const [selectedOption, setSelectedOption] = useState('month');
    const [temporaryOption, setTemporaryOption] = useState(selectedOption)
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const today = new Date();


    const setDropdown = () => {
        setTemporaryOption(selectedOption); // Reset temporary selection to confirmed option
        setShowDropdown(!showDropdown)
    }

    const [formValues, setFormValues] = useState<formValueInterface>({
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        fromDate: null,
        toDate: null,
        dateFilterType: 'month',
        defaultFilter: true,
        dateFilterChanged: false,
    })

    const [transactions, setTransactions] = useState<TransactionItem[]>([])

    useEffect(() => {
        handleUpdate()
        getCompanyLocations()
    }, [])

    useEffect(() => {
        getTransactions(currentPage)
    }, [formValues, currentPage, selectedLocationId])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Transaction History", url: "", showBackButton: false, showMainMenu: true, activeLink: "reports" });
    };

    const getTransactions = async (pageNo = 1) => {
        try {

            setLoading(true)
            let param;
            param = `?pageNo=${pageNo}`

            // Need to be considered only if filter by is done.
            if (formValues.filterChanged) {
                if (formValues.month && formValues.dateFilterType === 'month') {
                    param += "&month=" + formValues.month;
                    param += "&year=" + new Date().getFullYear();
                }
                if (formValues.year && formValues.dateFilterType === 'year') {
                    param += "&year=" + formValues.year;
                }
                if (formValues.fromDate && formValues.dateFilterType === 'custom') {
                    param += "&fromDate=" + formValues.fromDate;
                }
                if (formValues.toDate && formValues.dateFilterType === 'custom') {
                    param += "&toDate=" + formValues.toDate;
                }
            } else {
                if (formValues.month && formValues.dateFilterType === 'month') {
                    param += "&month=" + formValues.month;
                    param += "&year=" + new Date().getFullYear();
                }
            }
            if (selectedLocationId !== "0") {
                param += "&locationId=" + selectedLocationId;
            }

            const { data } = await axios.get(TransactionApi.CARWASH_OWNER_GET_TRANSACTION + param);
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const transaction_list = data.data.list;
                if (transaction_list.length > 0) {
                    setTransactions(transaction_list)
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Transaction list is empty');
                    setTransactions([]);
                }
            } else {
                console.log('Transaction list is not available or is not an array');
                setTransactions([]);
            }
            setLoading(false)
        } catch (error: any) {
            console.error('Error fetching Transaction list:', error.message);
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getCompanyLocations = async () => {
        try {
            const { data } = await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data?.error === 0 && Array.isArray(data?.data)) {
                const locationList: LocationItem[] = data.data;
                const transformedArray: Option[] = locationList.map((item) => ({
                    label: item.locationName,
                    value: item.locationId,
                }));
                setLocations([{ label: "All", value: 0 }, ...transformedArray]);
            } else {
                setLocations([{ label: "All", value: 0 }]);
            }
        } catch (error: any) {
            setLocations([{ label: "All", value: 0 }]);
            console.error("Error fetching locations:", error.message);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };
    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };
    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    // Handle radio button change
    const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTemporaryOption(event.target.id); // Update temporary selection
    };

    // Handle DatePicker changes
    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (date: Date | null) => {
        setter(date);
    };

    // Handle form submission
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent the default form submission behavior
        setSelectedOption(temporaryOption); // Confirm the selection
        let month = null;
        let year = null
        const today = new Date();
        if (temporaryOption === 'month') {
            month = today.getMonth() + 1
        } else {
            year = today.getFullYear()
        }
        let filterChanged = false;
        let dateFilterChanged = false;


        if (temporaryOption !== 'month') {
            filterChanged = true;
            dateFilterChanged = true;
        }

        if (filterChanged === true) {
            setFilterClass('filtered')
        } else {
            setFilterClass('')
        }
        let fromDate = null;
        if (startDate) {
            fromDate = moment(startDate).format('YYYY-MM-DD')
        }
        let toDate = null;
        if (endDate) {
            toDate = moment(endDate).format('YYYY-MM-DD')
        }
        // Get form values
        setFormValues({
            month: month,
            year: year,
            fromDate: fromDate,
            toDate: toDate,
            dateFilterType: temporaryOption,
            filterChanged: filterChanged,
            defaultFilter: (!filterChanged) ? true : false,
            dateFilterChanged: dateFilterChanged,
        });

        setCurrentPage(1)
        setDropdown()

    };

    //To clear form
    const handleClear = async () => {
        setTemporaryOption('month'); // Reset temporary selection
        setSelectedOption('month');
        setStartDate(null);
        setEndDate(null);
        const today = new Date();
        setFormValues({
            month: today.getMonth() + 1,
            year: today.getFullYear(),
            fromDate: null,
            toDate: null,
            dateFilterType: 'month',
            defaultFilter: true,
            dateFilterChanged: false,
        });
        await setCurrentPage(1)
        setDropdown();
        setFilterClass('')

    }

    // To format date
    const formatDate = (transDate: string) => {
        const date = moment(transDate);
        const formattedDate = date.format('MM/DD/YYYY');
        return formattedDate
    }

    function capitalizeFirstLetter(criteria: string) {
        return criteria.charAt(0).toUpperCase() + criteria.slice(1);
    }

    return (

        <div className='manage-carwash-container order-list-container'>
            <Container>
                <Breadcrumb>
                    <Breadcrumb.Item onClick={() => { itemClick(`/${pageURLs.dashboard}`) }}>Dashboard</Breadcrumb.Item>
                    <Breadcrumb.Item active>Transaction History</Breadcrumb.Item>
                </Breadcrumb>
                <div className="manage-header-block d-flex justify-content-between align-items-center">
                    <div className="search-wrap w-100">
                        <div className="result-group d-flex align-items-center">

                            <p className='title f-bold'>Results for</p>
                            <CWSelect
                                id="selectlocation"
                                name="selectlocation"
                                options={locations}
                                label="Select Location"
                                dropdownGap={0}
                                dropdownPosition="auto"
                                placeholder="Select Location"
                                values={locations.filter((option) => option.value.toString() === selectedLocationId)}
                                onChange={(selected: any) => {
                                    const value = Array.isArray(selected) && selected.length > 0 ? selected[0].value : 0;
                                    setSelectedLocationId(value.toString());
                                    setCurrentPage(1);
                                }}
                            />
                            <Dropdown className={"filter-dropdown " + filterClass} align="start" show={showDropdown} onToggle={setDropdown}>
                                <Dropdown.Toggle id="dropdown-autoclose-true" onClick={setDropdown}>
                                    Filter by
                                    <FilterIcon />
                                    {formValues.dateFilterType && capitalizeFirstLetter(formValues.dateFilterType)}

                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <h6>Filter by</h6>
                                    <form onSubmit={handleSubmit}>

                                        <div className="radio-field-group d-flex flex-column">
                                            <CWRadio
                                                id="month"
                                                name="timeframe"
                                                label="Current Month"
                                                value="month"
                                                checked={temporaryOption === 'month'}
                                                onChange={handleRadioChange}
                                            />
                                            <CWRadio
                                                id="year"
                                                name="timeframe"
                                                label="Current Year"
                                                value="year"
                                                checked={temporaryOption === 'year'}
                                                onChange={handleRadioChange}
                                            />
                                            <CWRadio
                                                id="custom"
                                                name="timeframe"
                                                label="Custom"
                                                value="custom"
                                                checked={temporaryOption === 'custom'}
                                                onChange={handleRadioChange}
                                            />
                                        </div>
                                        {temporaryOption === "custom" ? (<div className="calendar-field d-flex flex-column">
                                            <DatePicker
                                                showIcon
                                                selected={startDate}
                                                onChange={handleDateChange(setStartDate)}
                                                placeholderText='From'
                                            />
                                            <DatePicker
                                                showIcon
                                                selected={endDate}
                                                onChange={handleDateChange(setEndDate)}
                                                placeholderText='To'
                                            />
                                        </div>) : null}

                                        <div className="button-block d-flex justify-content-between">
                                            <Button title="Reset" variant="light" className="light-btn" onClick={() => handleClear()}>Reset</Button>
                                            <Button title="Apply" variant="primary" className="primary-btn" type="submit">Apply</Button>
                                        </div>
                                    </form>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Location</th>
                                {/* <th>Transaction ID</th> */}
                                <th>Transaction For</th>
                                <th>Amount</th>
                                
                            </tr>
                        </thead>
                        <tbody>
                            {transactions && transactions.length ?
                                (transactions.map((item, index) => (
                                    <tr key={index}>
                                        <td>{formatDate(item.TransDate)}</td>
                                        {/* <td>{item.TransID}</td> */}
                                        <td>{item.LocationName ?? ''}</td>
                                        <td>{ItemTypeEnum[item.ItemType as keyof typeof ItemTypeEnum] || 'Unknown Type'}</td>
                                        <td>${item.Price}</td>
                                       
                                    </tr>
                                )))
                                : (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={5} >No data found.</td>
                                    </tr>
                                )}

                        </tbody>
                    </Table>

                    {transactions.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
                </div>
            </Container >
            {loading && <SmallLoader />}

        </div >
    )
}

export default CWOTransactionHistory;
